export default function PyVEditPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Privacy Policy - Python Runner &amp; Editor
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-04-07">April 7, 2026</time>
            {" · "}Last updated:{" "}
            <time dateTime="2026-09-25">September 25, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built the{" "}
            <strong>Python Runner &amp; Editor</strong> (PyVEdit) app as an
            ad-supported application. This Privacy Policy explains what
            information PyVEdit may process, how that information is used, and
            the choices you have when using the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Python scripts and project files:</strong> code files you
              write, open, or import are processed locally on your device using
              the embedded Chaquopy Python runtime. They are never uploaded to
              our servers.
            </li>
            <li>
              <strong>Installed Python packages:</strong> packages you install
              via the in-app package manager are downloaded from the internet
              (PyPI or Chaquopy package index) and stored locally on your
              device.
            </li>
            <li>
              <strong>Script output and charts:</strong> program output and
              matplotlib charts generated at runtime are displayed locally and
              are never transmitted to our servers.
            </li>
            <li>
              <strong>App preferences:</strong> your theme choice, editor
              settings, and preferences are stored locally on your device.
            </li>
            <li>
              <strong>Usage and ad data:</strong> if ads are enabled, AdMob or
              related Google Mobile Ads services may collect device and
              advertising data as described in Google&apos;s own privacy
              documentation.
            </li>
            <li>
              <strong>Analytics and diagnostics:</strong> Firebase Analytics and
              Firebase Crashlytics may collect app stability, usage patterns, and
              crash reports to improve reliability and user experience.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              To let you write, edit, and run Python scripts directly on your
              device using the embedded Python runtime.
            </li>
            <li>
              To render matplotlib charts and display program output inline.
            </li>
            <li>
              To let you install and manage Python packages at runtime.
            </li>
            <li>
              To remember your projects, editor settings, and preferences.
            </li>
            <li>
              To improve app performance, reliability, and product quality.
            </li>
            <li>
              To display ads in supported versions of the app, where applicable.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Local Storage</h2>
          <p>
            PyVEdit is designed to keep your data on your device. Python scripts,
            project files, installed packages, notebook files, and preferences
            are stored locally and are not uploaded to any server. Data remains
            on your device until you remove it, clear the app&apos;s storage, or
            uninstall the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Network Access During Package Installation
          </h2>
          <p>
            When you install a Python package via the in-app package manager,
            PyVEdit connects to the internet to download the package from PyPI or
            the Chaquopy package repository. Only the package name and version
            are sent as part of a standard HTTPS download request. No personal
            data, script content, or file data is transmitted during this
            process.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Advertising</h2>
          <p>
            PyVEdit may use <strong>Google AdMob</strong> to show banner,
            interstitial, and rewarded ad formats. AdMob may collect and process
            identifiers, device information, approximate location, and ad
            interaction data to deliver and measure ads. You can remove ads via
            an in-app purchase.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            In-App Purchases
          </h2>
          <p>
            PyVEdit offers an optional in-app purchase to remove advertisements.
            Purchases are processed through Google Play and are subject to
            Google&apos;s terms and privacy policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            On iPhone and iPad
          </h2>
          <p>
            The iOS version of PyVEdit works differently from the Android
            version in a few ways that matter for your privacy:
          </p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Your code runs on your device.</strong> Python runs inside
              the app itself, using a WebAssembly build of Python (Pyodide).
              Your scripts, project files, notebooks, and program output are
              never uploaded to our servers.
            </li>
            <li>
              <strong>No package downloads.</strong> The Python libraries the
              iOS app offers are built into the app, so it never contacts a
              package index.
            </li>
            <li>
              <strong>Web requests your code makes.</strong> If your own code
              fetches a web page or API (for example with{" "}
              <code>requests</code> or <code>urllib</code>), the request goes
              directly from your device to the address in your code. It does
              not pass through our servers, and we do not see it.
            </li>
            <li>
              <strong>Purchases.</strong> Pro (a monthly subscription or a
              one-time lifetime purchase) is sold through Apple&apos;s App Store
              and is subject to Apple&apos;s terms and privacy policy. We do not
              receive your payment details.
            </li>
            <li>
              <strong>Advertising.</strong> The iOS app may show banner and
              interstitial ads from Google AdMob, as described above. It does
              not ask for permission to track you across other companies&apos;
              apps and websites, so iOS does not share your device&apos;s
              advertising identifier with it. You can review your ad privacy
              choices from the app&apos;s Settings where your region requires it.
            </li>
            <li>
              <strong>No AI features.</strong> The iOS app does not include
              COSMO AI, so no code or prompts are sent to any AI service.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Third-Party Services
          </h2>
          <p>PyVEdit may rely on third-party services such as:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Google Play Services</li>
            <li>Google AdMob</li>
            <li>Firebase Analytics</li>
            <li>Firebase Crashlytics</li>
            <li>Firebase Remote Config</li>
            <li>Chaquopy Python SDK (local runtime, no data transmitted)</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Sharing</h2>
          <p>
            We do not sell your scripts, project files, or personal data. Data
            may be shared only with service providers required to operate app
            features, such as advertising, analytics, app distribution, or
            diagnostics infrastructure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Retention</h2>
          <p>
            Local app data remains on your device until you remove it, clear the
            app&apos;s storage, or uninstall the app. Data collected by
            third-party services such as AdMob and Firebase is retained according
            to those providers&apos; own policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Children&apos;s Privacy
          </h2>
          <p>
            PyVEdit is not intended for children under 13, and we do not
            knowingly collect personal information from children through the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Security</h2>
          <p>
            We take reasonable steps to protect data processed by the app, but no
            method of storage or transmission is completely secure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Your Choices</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              You can delete individual scripts and projects from inside the app.
            </li>
            <li>
              You can uninstall Python packages you no longer need via the
              package manager.
            </li>
            <li>
              You can uninstall the app to remove all locally stored data from
              your device.
            </li>
            <li>You can purchase ad-free to disable all advertising.</li>
            <li>
              You can manage ad personalization and advertising controls through
              your device and Google settings.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. When we do, we
            will post the updated version on this page and revise the effective
            date.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Us</h2>
          <p>
            For privacy-related questions about Python Runner &amp; Editor,
            contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
